import React, { useMemo } from "react";

const isBlank = (v) => (v ?? "").toString().trim().length === 0;

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const getAlignValue = (style) => {
  const v = String(style?.textAlign || style?.alignment || "").toLowerCase().trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return "center";
};

const getAlignClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "text-left";
  if (a === "right") return "text-right";
  return "text-center";
};

const getSelfAlignClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "mr-auto";
  if (a === "right") return "ml-auto";
  return "mx-auto";
};

const getRowJustifyClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "justify-start";
  if (a === "right") return "justify-end";
  return "justify-center";
};

const parseItems = (config) => {
  if (Array.isArray(config?.items)) return config.items;

  const raw = config?.itemsJson;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const openCatalog = (catalogId, baseUrl) => {
  const id = (catalogId || "").trim();
  if (!id) return;
  const base = (baseUrl || "https://smartcatalog.shop").replace(/\/+$/, "");
  window.open(`${base}/${encodeURIComponent(id)}`, "_blank", "noopener,noreferrer");
};

export default function CategorySection2({ config, style }) {
  const bg = buildBg(style, "bg-white");
  const alignClass = getAlignClass(style);
  const selfAlignClass = getSelfAlignClass(style);
  const rowJustifyClass = getRowJustifyClass(style);

  const items = useMemo(() => parseItems(config), [config]);
  const maxItems = Number.isFinite(Number(config?.maxItems)) ? Number(config.maxItems) : 8;
  const shown = items.slice(0, Math.max(1, maxItems));

  const buttonText = config?.buttonText ?? "Open Catalog";
  const baseUrl = config?.baseUrl || "https://smartcatalog.shop";

  const btnStyle = {
    backgroundColor: style?.primaryBtnBg || undefined,
    color: style?.primaryBtnText || undefined,
  };

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`py-16 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className={`${alignClass} mb-10`}>
          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-5"
          >
            {config?.title}
          </h2>

          <p
            style={{ color: style?.subtitleColor || undefined }}
            className={`text-xl text-gray-600 max-w-3xl ${selfAlignClass}`}
          >
            {config?.subtitle}
          </p>
        </div>

        <div className="relative">
          <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory">
            {shown.map((it, idx) => (
              <button
                key={it.id || idx}
                type="button"
                onClick={() => openCatalog(config?.catalogId, baseUrl)}
                style={{
                  backgroundColor: style?.categoryCardBg || undefined,
                  borderColor: style?.categoryCardBorder || undefined,
                }}
                className="snap-start min-w-[280px] sm:min-w-[320px] rounded-3xl overflow-hidden border shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <div className="h-52 bg-gray-100 relative">
                  {it.image ? (
                    <img
                      src={it.image}
                      alt={it.title || "item"}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200" />
                  )}

                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        style?.categoryImageOverlayColor ||
                        "linear-gradient(to top, rgba(0,0,0,0.50), transparent)",
                    }}
                  />

                  <div className={`absolute bottom-4 left-4 right-4 text-white ${alignClass}`}>
                    <div
                      style={{ color: style?.categoryCardTitleColor || "#ffffff" }}
                      className="text-lg font-bold"
                    >
                      {it.title || "Untitled item"}
                    </div>

                    {!isBlank(it.subtitle) && (
                      <div
                        style={{ color: style?.categoryCardSubtitleColor || "rgba(255,255,255,0.8)" }}
                        className="text-sm line-clamp-1"
                      >
                        {it.subtitle}
                      </div>
                    )}
                  </div>
                </div>

                <div className={`p-5 ${alignClass}`}>
                  <div
                    style={{ color: style?.categoryCardSubtitleColor || undefined }}
                    className="text-sm text-gray-600"
                  >
                    {config?.viewInCatalogText || "Tap to open in catalog"}
                  </div>

                  <div
                    style={{ color: style?.categoryLinkColor || undefined }}
                    className={`mt-3 inline-flex items-center font-semibold ${selfAlignClass}`}
                  >
                    {buttonText || "Open"} <span className="ms-2">→</span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className={`mt-8 flex flex-wrap items-center gap-4 ${rowJustifyClass}`}>
            {!isBlank(config?.catalogId) && !isBlank(buttonText) && (
              <button
                type="button"
                style={style?.primaryBtnBg ? btnStyle : undefined}
                onClick={() => openCatalog(config?.catalogId, baseUrl)}
                className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 hover:scale-105 ${
                  style?.primaryBtnBg ? "" : "bg-blue-600 hover:bg-blue-700 text-white shadow-xl"
                }`}
              >
                {buttonText}
              </button>
            )}

            {!isBlank(config?.hintText) && (
              <div
                style={{ color: style?.categoryCardSubtitleColor || undefined }}
                className="text-sm text-gray-500"
              >
                {config?.hintText}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}